import { Box, Flex, Spinner, Stack } from "@chakra-ui/react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InvoiceFormData } from "@/types";
import { useClientDefaultInvoiceDate } from "./useClientDefaultInvoiceDate";

import { FeesAndExpensesSkeleton } from "./FeesAndExpensesSkeleton";
import { Button, ErrorMessage, Heading } from "@digital-services-gard-as/anchor-react";

import { Expenses } from "./Expenses";
import { OtherExpenses } from "./OtherExpenses";
import { LawyerAndCounselFees } from "./LawyerAndCounselFees";
import { AdvanceOfFunds } from "./AdvanceOfFunds";
import { TotalAmount } from "./TotalAmount";
import { InvoiceDetails } from "./InvoiceDetails";
import { useQuery } from "@tanstack/react-query";
import { getClaimRecipientDetailsQueryOptions } from "@/api/queryOptions/claimsApiQueryOptions";
import { InvoiceFromOcrDto } from "@/api/invoiceApi";
import React, { useCallback } from "react";
import { InvoiceConfirmation } from "./InvoiceConfirmation";

type FeesAndExpensesProps = {
    totalAmount: number;
    ocrResponse?: InvoiceFromOcrDto;
    submitError?: string;
    invoiceId: string;
    isSubmittingInvoice?: boolean;
    userRole: string;
    crmId?: string;
    isLoadingOcr?: boolean;
};

export const FeesAndExpenses = React.memo(function FeesAndExpenses({
    totalAmount,
    ocrResponse,
    submitError,
    invoiceId: _invoiceId,
    userRole,
    isSubmittingInvoice,
    crmId,
    isLoadingOcr
}: FeesAndExpensesProps) {
    const { t } = useTranslation(["main", "invoices", "errorMessages"]);
    const navigate = useNavigate();
    const {
        setValue,
        watch,
        formState: { dirtyFields }
    } = useFormContext<InvoiceFormData>();

    const watchInvoiceAttachment = watch("invoiceAttachment");
    const claimRef = watch("claimRef") ?? "";

    const isUploadingAttachment = Boolean(watchInvoiceAttachment?.isUploading);
    const isAttachmentBlockingSubmit = Boolean(
        watchInvoiceAttachment &&
        (!watchInvoiceAttachment.isUploaded || !watchInvoiceAttachment.docId || watchInvoiceAttachment.uploadFailed)
    );
    const isFinishDisabled = isSubmittingInvoice || isUploadingAttachment || isAttachmentBlockingSubmit;

    const { data: invoiceRecipient, isLoading: isLoadingClaimsInfo } = useQuery(
        getClaimRecipientDetailsQueryOptions(claimRef, crmId)
    );

    useClientDefaultInvoiceDate({
        isDirtyInvoiceDate: dirtyFields.invoiceDate,
        userRole,
        setValue
    });

    const handleBackToOverview = useCallback(() => {
        navigate("/");
    }, [navigate]);

    if (isLoadingOcr) {
        return <FeesAndExpensesSkeleton />;
    }

    return (
        <Box flex={{ base: "unset", lg: "2" }} minW={0} w="100%">
            <Heading size="lg" id="feesAndExpensesTitle">
                {t("invoices:createInvoice.feesAndExpensesTitle")}
            </Heading>
            <Stack gap={4} mt={4}>
                <InvoiceDetails />
                <Expenses userRole={userRole} />

                {userRole !== "Client" && (
                    <>
                        <OtherExpenses invoiceRecipient={invoiceRecipient} ocrResponse={ocrResponse} crmId={crmId} />
                        <LawyerAndCounselFees />
                        <AdvanceOfFunds />
                    </>
                )}
                <TotalAmount
                    totalAmountForm={totalAmount}
                    totalAmountOcr={ocrResponse?.totalCosts}
                    ocrCurrency={ocrResponse?.currency}
                />
                <InvoiceConfirmation
                    invoiceRecipient={invoiceRecipient}
                    isLoadingInvoiceRecipient={isLoadingClaimsInfo}
                    claimRef={claimRef}
                />
                {submitError && (
                    <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} borderRadius="md" boxShadow="xs">
                        <ErrorMessage data-testid="submitError">{submitError}</ErrorMessage>
                    </Box>
                )}

                <Box bg="white" p={{ base: "1rem", md: "1.5rem" }} borderRadius="md" boxShadow="xs">
                    <Flex mt={2} gap={4}>
                        <Button id="finishBtn" variant="primary" type="submit" disabled={isFinishDisabled}>
                            {isSubmittingInvoice ? <Spinner /> : t("invoices:createInvoice.finishBtn")}
                        </Button>
                        <Button id="backToOverview" variant="secondary" type="button" onClick={handleBackToOverview}>
                            {t("invoices:backToOverview")}
                        </Button>
                    </Flex>
                </Box>
            </Stack>
        </Box>
    );
});
